import React from 'react';
import { Switch as MuiSwitch, Box, styled } from '@mui/material';
import type { SwitchProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Custom switch variants
export type SwitchVariant = 'default' | 'ios' | 'android';
export type SwitchSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomSwitchProps extends Omit<SwitchProps, 'size'> {
  variant?: SwitchVariant;
  size?: SwitchSize;
  label?: string;
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
  description?: string;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
}

// Styled components
const StyledSwitch = styled(MuiSwitch)<{
  variant?: SwitchVariant;
  size?: SwitchSize;
  error?: boolean;
}>(({ theme, variant: switchVariant, size, error }) => {
  const sizeConfig = {
    small: {
      width: 34,
      height: 20,
      padding: 0,
      thumbSize: 16,
      trackBorderRadius: 10,
    },
    medium: {
      width: 42,
      height: 26,
      padding: 0,
      thumbSize: 20,
      trackBorderRadius: 13,
    },
    large: {
      width: 50,
      height: 30,
      padding: 0,
      thumbSize: 24,
      trackBorderRadius: 15,
    },
  };

  const currentSize = sizeConfig[size || 'medium'];
  const trackColor = error ? theme.palette.error.main : theme.palette.primary.main;

  const baseStyles = {
    width: currentSize.width,
    height: currentSize.height,
    padding: currentSize.padding,
    '& .MuiSwitch-switchBase': {
      padding: (currentSize.height - currentSize.thumbSize) / 2,
      '&.Mui-checked': {
        transform: `translateX(${currentSize.width - currentSize.height}px)`,
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: trackColor,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: trackColor,
        border: `6px solid #fff`,
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[400],
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.3,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box' as const,
      width: currentSize.thumbSize,
      height: currentSize.thumbSize,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    '& .MuiSwitch-track': {
      borderRadius: currentSize.trackBorderRadius,
      backgroundColor: theme.palette.grey[300],
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  };

  // Variant-specific styles
  const variantStyles = {
    default: {},
    ios: {
      '& .MuiSwitch-thumb': {
        background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
        border: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .MuiSwitch-track': {
        background: `linear-gradient(145deg, ${theme.palette.grey[200]}, ${theme.palette.grey[300]})`,
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        background: `linear-gradient(145deg, ${trackColor}, ${alpha(trackColor, 0.8)})`,
      },
    },
    android: {
      '& .MuiSwitch-thumb': {
        backgroundColor: '#fafafa',
        border: `2px solid ${theme.palette.grey[400]}`,
      },
      '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
        border: `2px solid ${trackColor}`,
      },
    },
  };

  return {
    ...baseStyles,
    ...variantStyles[switchVariant || 'default'],
  };
});

const SwitchContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'labelPlacement',
})<{ labelPlacement?: 'start' | 'end' | 'top' | 'bottom' }>(({ labelPlacement }) => {
  const flexDirectionMap: Record<string, 'row' | 'column' | 'row-reverse' | 'column-reverse'> = {
    start: 'row-reverse',
    end: 'row',
    top: 'column-reverse',
    bottom: 'column',
  };

  return {
    display: 'flex',
    flexDirection: flexDirectionMap[labelPlacement || 'end'],
    alignItems: labelPlacement === 'top' || labelPlacement === 'bottom' ? 'flex-start' : 'center',
    gap: 12,
  };
});

const LabelContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& .switch-label': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
  '& .switch-description': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  '& .switch-helper-text': {
    fontSize: '0.75rem',
    marginTop: 4,
    '&.error': {
      color: theme.palette.error.main,
    },
  },
}));

export const Switch: React.FC<CustomSwitchProps> = ({
  variant = 'default',
  size = 'medium',
  label,
  labelPlacement = 'end',
  description,
  error = false,
  helperText,
  loading = false,
  disabled,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const switchElement = (
    <StyledSwitch
      {...props}
      variant={variant}
      size={size === 'large' ? 'medium' : size} // MUI Switch doesn't have large size
      error={error}
      disabled={isDisabled}
    />
  );

  if (!label && !description && !helperText) {
    return switchElement;
  }

  return (
    <SwitchContainer labelPlacement={labelPlacement}>
      {switchElement}
      <LabelContainer>
        {label && <div className="switch-label">{label}</div>}
        {description && <div className="switch-description">{description}</div>}
        {helperText && (
          <div className={`switch-helper-text ${error ? 'error' : ''}`}>{helperText}</div>
        )}
      </LabelContainer>
    </SwitchContainer>
  );
};

export default Switch;
